import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dmsApi } from '../../api/dms.js';
import { useRealtime } from '../../hooks/useRealtime.js';

export default function DocumentViewer() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  function reload() {
    dmsApi.getDocument(id).then((d) => setDoc(d?.document));
    dmsApi.versions(id).then((d) => setVersions(d?.items || []));
  }
  useEffect(reload, [id]);

  useRealtime({
    type: 'document',
    id,
    events: {
      'document:version:uploaded': () => { setInfo('A new version was uploaded'); reload(); },
    },
  });

  async function uploadVersion(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const notes = window.prompt('Change notes? (optional)') || '';
    try {
      await dmsApi.newVersion(id, file, notes);
      e.target.value = '';
      reload();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  async function approve() {
    try {
      await dmsApi.approve(id, 'Approval requested');
      setInfo('Approval requested — workflow triggered if configured.');
      reload();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  if (!doc) return <div className="muted">Loading…</div>;

  const isPdf = (doc.mime_type || '').includes('pdf');
  const isImage = (doc.mime_type || '').startsWith('image/');

  return (
    <div className="dms-layout">
      <section className="dms-main">
        <header className="page-header">
          <div>
            <Link to="/dms" className="muted small">← Library</Link>
            <h1>{doc.name}</h1>
            <p className="muted">v{doc.version} · {doc.mime_type || 'unknown'} · <span className={`chip status-${doc.status === 'approved' ? 'approved' : doc.status === 'in_review' ? 'pending' : 'cancelled'}`}>{doc.status}</span></p>
          </div>
          <div className="actions">
            <a href={doc.file_url} target="_blank" rel="noreferrer" className="btn ghost">Open</a>
            <a href={doc.file_url} download className="btn ghost">Download</a>
            <label className="btn ghost">
              Upload new version
              <input type="file" onChange={uploadVersion} style={{ display: 'none' }} />
            </label>
            {doc.status !== 'in_review' && doc.status !== 'approved' && (
              <button className="btn primary" onClick={approve}>Request approval</button>
            )}
          </div>
        </header>

        {error && <div className="alert error">{error}</div>}
        {info && <div className="alert info">{info}</div>}

        <div className="dms-preview">
          {isPdf && <iframe src={doc.file_url} title={doc.name} />}
          {isImage && <img src={doc.file_url} alt={doc.name} />}
          {!isPdf && !isImage && (
            <div className="muted center" style={{ padding: 60 }}>
              Preview not available for this file type.<br />
              <a href={doc.file_url} target="_blank" rel="noreferrer" className="btn primary">Open file</a>
            </div>
          )}
        </div>
      </section>

      <aside className="dms-side">
        <div className="card">
          <h3>Version history</h3>
          <ul className="version-list">
            {versions.map((v) => (
              <li key={v.id}>
                <div className="row">
                  <strong>v{v.version_no}</strong>
                  <a href={v.file_url} target="_blank" rel="noreferrer" className="muted small">open</a>
                </div>
                <div className="muted small">{v.uploaded_by_name || '—'} · {new Date(v.created_at).toLocaleString()}</div>
                {v.change_notes && <div className="muted small">"{v.change_notes}"</div>}
              </li>
            ))}
            {versions.length === 0 && <li className="muted">No versions.</li>}
          </ul>
        </div>
      </aside>
    </div>
  );
}
